import React, { useContext, useEffect, useState } from "react";
import { IDyspepsiaSymptomsDataPayload } from "../../../../../../types";
import { PatientContext } from "../../../../patientFromContext/PatientsContext";
import { showToast } from "../../../../../../utils/utility";
import { useDirectusApi } from "../../../../../../libs/data-access";
import { useSearchParams } from "react-router-dom";

export default function useDyspepsiaSymptoms() {
  const [searchParams] = useSearchParams();
  const patient_id = searchParams.get("patient_id");
  const visit_id = searchParams.get("visit_id");
  const { postDirectData, fetchDirectData, patchDirectData } = useDirectusApi();
  const {
    activeTab,
    isBtnNextClick,
    setIsBtnDisable,
    setIsBtnLoading,
    handleNextTab,
    isFormUpdating,
    setIsFormUpdating,
  } = useContext(PatientContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [gallStoneID, setGallStoneID] = useState<number | undefined>(undefined);

  const [errors, setErrors] = useState<any>({});
  const [stoolFreq, setStoolFreq] = React.useState<string>("");
  const [bristolStoolScore, setBristolStoolScore] = React.useState<{
    usual: string;
    worst: string;
  }>({
    usual: "",
    worst: "",
  });

  const [symptomsOfDyspepsia, setSymptomsOfDyspepsia] = React.useState<
    { label: string; selected: boolean; remarks: string; value: number }[]
  >([
    {
      label: "Bothersome epigastric pain",
      selected: false,
      remarks: "",
      value: 1,
    },
    {
      label: "Bothersome epigastric burning",
      selected: false,
      remarks: "",
      value: 2,
    },
    {
      label: "Bothersome early satiety",
      selected: false,
      remarks: "",
      value: 3,
    },
    {
      label: "Bothersome postprandial fullness",
      selected: false,
      remarks: "",
      value: 4,
    },
    {
      label: "Overall duration of symptoms ≥ 3m",
      selected: false,
      remarks: "",
      value: 5,
    },
    {
      label: "Criteria fulfilled for ≥ 6m",
      selected: false,
      remarks: "",
      value: 6,
    },
  ]);

  const [dyspepsiaScoringIndex, setDyspepsiaScoringIndex] = React.useState<
    { label: string; selected: boolean; score: string }[]
  >([
    { label: "Epigastric pain", selected: false, score: "" },
    { label: "Retrosternal pain", selected: false, score: "" },
    { label: "Regurgitation", selected: false, score: "" },
    { label: "Nausea", selected: false, score: "" },
    {
      label: "Vomiting",
      selected: false,
      score: "",
    },
    { label: "Belching", selected: false, score: "" },
    { label: "Early Satiety", selected: false, score: "" },
    { label: "Dysphagia", selected: false, score: "" },
  ]);

  const [finalLeadScoreForDyspepsia, setFinalLeadScoreForDyspepsia] =
    useState<string>("");

  const [scoringIndex, setScoringIndex] = React.useState<
    { label: string; selected: boolean; score: string }[]
  >([
    { label: "Very mild", selected: false, score: "1" },
    { label: "Mild", selected: false, score: "2" },
    { label: "Moderate", selected: false, score: "3" },
    { label: "Severe", selected: false, score: "4" },
    { label: "Very severe", selected: false, score: "5" },
  ]);

  const validateField = (key: string, subKey: string | undefined, value: any, extraData?: any) => {
    let errorMsg = "";
    const sKey = subKey ? String(subKey) : key;

    if (key === "bristol_stool_score") {
      if (isNaN(Number(value))) {
        errorMsg = "Not a valid number";
      } else if (Number(value) > 7) {
        errorMsg = `${extraData?.label || "Score"} should be less than 7`;
      }
    }

    setErrors((prev: any) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [sKey]: errorMsg,
      }
    }));
    return errorMsg;
  };

  const validateAll = () => {
    let hasError = false;

    if (validateField("bristol_stool_score", "usual", bristolStoolScore.usual, { label: "Usual Score" }) !== "") hasError = true;
    if (validateField("bristol_stool_score", "worst", bristolStoolScore.worst, { label: "Worst Score" }) !== "") hasError = true;

    return hasError;
  };


  useEffect(() => {
    validateAll()
  }, [bristolStoolScore])

  const handleSubmitGallstone = async () => {
    if (patient_id && visit_id) {
      let payload: IDyspepsiaSymptomsDataPayload =
        await handlePayloadForGallStonesForm(patient_id, visit_id);
      setIsBtnLoading(true);
      if (isFormUpdating) {
        const res = await patchDirectData(
          `items/gallstones_history/${gallStoneID}`,
          payload
        );
        if (res.isSuccess) {
          showToast({
            type: "success",
            title: "gallstones history updated successfully",
            message: "Gallstones history Updated",
          });
          handleNextTab();
        } else {
          showToast({
            type: "error",
            message: res?.message || "Failed to add gallstones history",
          });
        }
      } else {
        const res = await postDirectData(`items/gallstones_history`, payload);
        if (res.isSuccess) {
          showToast({
            type: "success",
            title: "Added gallstones history successfully",
            message: "Gallstones history added",
          });
          handleNextTab();
        } else {
          showToast({
            type: "error",
            message: res?.message || "Failed to add gallstones history",
          });
        }
      }
      setIsBtnLoading(false);
    } else {
      showToast({
        type: "error",
        title: "Please try again",
        message: "User id not found !",
      });
    }
  };

  useEffect(() => {
    setIsBtnDisable(false);
    if (isBtnNextClick && activeTab === "dyspepsia_symptoms") {
      if (validateAll()) {
        showToast({
          type: "error",
          title: "Validation Error",
          message: "Please fix all validation errors before submitting.",
        });
        return;
      }
      handleSubmitGallstone();
    }
  }, [isBtnNextClick, activeTab,]);

  useEffect(() => {
    if (activeTab === "dyspepsia_symptoms") {
      getDetailsByVisitId();
    }
  }, [activeTab]);

  useEffect(() => {
    let count = 0;
    let dataArr = dyspepsiaScoringIndex.filter((e) => e?.selected);
    dataArr?.forEach((each) => {
      count = each?.score ? count + +each?.score : count;
    });
    setFinalLeadScoreForDyspepsia(
      count ? String(Math.round(count)) : ""
    );
  }, [dyspepsiaScoringIndex]);

  const handlePayloadForGallStonesForm = async (
    userId: string,
    visit_id: string
  ) => {
    let payload: IDyspepsiaSymptomsDataPayload = {
      user_id: userId,
      visit_id: visit_id,
      stool_frequency: stoolFreq,
      bristol_stool_usual_score: bristolStoolScore?.usual,
      bristol_stool_worst_score: bristolStoolScore?.worst,
      symptoms_of_dyspepsia: symptomsOfDyspepsia
        ?.filter((e) => e?.selected)
        ?.map((e) => ({
          symptoms_name: e?.label,
          is_symptom: e?.selected,
          remarks: e?.remarks,
        })),
      dyspepsia_scoring_index: dyspepsiaScoringIndex
        ?.filter((e) => e?.selected)
        .map((e) => ({
          dyspepsia_name: e?.label,
          status: e?.selected,
          score: e?.score,
        })),
      dyspepsia_final_score: finalLeadScoreForDyspepsia ?? "",
    };
    return payload;
  };

  //  APi Function

  const getDetailsByVisitId = async () => {
    setIsLoading(true);
    const res = await fetchDirectData(
      `items/gallstones_history?filter[visit_id][_eq]=${visit_id}`
    );
    if (res.isSuccess) {
      if (res?.data?.length) {
        setIsFormUpdating(true);
        let data: IDyspepsiaSymptomsDataPayload = res?.data[0];
        setGallStoneID(data?.id);


        setStoolFreq(data?.stool_frequency);
        setBristolStoolScore({
          usual: data?.bristol_stool_usual_score,
          worst: data?.bristol_stool_worst_score,
        });

        if (data?.symptoms_of_dyspepsia?.length) {
          let stateData = symptomsOfDyspepsia;
          data?.symptoms_of_dyspepsia?.forEach((e) => {
            const index = stateData.findIndex(
              (each) =>
                each?.label?.toLowerCase()?.replaceAll(" ", "_") ==
                e?.symptoms_name?.toLowerCase()?.replaceAll(" ", "_")
            );
            stateData[index].selected = true;
            stateData[index].remarks = e?.remarks;
          });
          setSymptomsOfDyspepsia(stateData);
        }
        setFinalLeadScoreForDyspepsia(data?.dyspepsia_final_score ?? 0);

        if (data?.dyspepsia_scoring_index?.length) {
          let stateData = dyspepsiaScoringIndex;
          data?.dyspepsia_scoring_index?.forEach((e) => {
            const index = stateData.findIndex(
              (each) =>
                each?.label?.toLowerCase()?.replaceAll(" ", "_") ==
                e?.dyspepsia_name?.toLowerCase()?.replaceAll(" ", "_")
            );
            stateData[index].selected = true;
            stateData[index].score = e?.score;
          });
          setDyspepsiaScoringIndex(stateData);
        }


      } else {
        setIsFormUpdating(false);
      }
    } else {
      showToast({
        type: "error",
        message: "Something went wrong",
      });
    }
    setIsLoading(false);
  };

  return {
    stoolFreq,
    setStoolFreq,
    bristolStoolScore,
    setBristolStoolScore,
    symptomsOfDyspepsia,
    setSymptomsOfDyspepsia,
    dyspepsiaScoringIndex,
    setDyspepsiaScoringIndex,
    finalLeadScoreForDyspepsia,
    scoringIndex,
    errors,
    validateField,
  };
}
